/**
 * Portal i18n bootstrap.
 *
 * Locale files live in src/locales/{en,nl}/<namespace>.json. Each
 * namespace maps to a portal area so multiple authors can edit
 * translations in parallel without merge conflicts.
 *
 * Convention:
 *   - common      → buttons, labels, generic words used everywhere
 *   - nav         → sidebar, top bar, breadcrumbs
 *   - auth        → login, signup, sso, invitation flows
 *   - dashboard   → /portal/dashboard variants
 *   - billing     → billing, invoices, upgrade requests
 *   - chat        → /portal/chat (readiness strip, empty states, sessions)
 *   - profile     → /portal/profile
 *   - connections → providers, servicenow, credentials
 *   - admin       → /admin/* pages
 *   - governance  → /governance/* pages
 *   - legal       → /legal/* pages
 *   - support     → /portal/support
 *   - marketing   → public pages (Home, Pricing, MarketingHome)
 *   - misc        → integration configs, device auth, themes config
 *   - operations  → /portal/operations, the Operations Board
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Catalogue counts, resolved from serac-labs/serac@main by the frontend
// prebuild and committed as source. See portal/scripts/count-catalog.ts.
import { CATALOG_COUNTS } from './generated/catalog-counts';

// English (source language) — bundled. Each namespace is a small JSON
// shipped alongside the page that owns it; we import them statically so
// the language toggle is instant (no network).
import enCommon from './locales/en/common.json';
import enNav from './locales/en/nav.json';
import enAuth from './locales/en/auth.json';
import enDashboard from './locales/en/dashboard.json';
import enBilling from './locales/en/billing.json';
import enChat from './locales/en/chat.json';
import enProfile from './locales/en/profile.json';
import enConnections from './locales/en/connections.json';
import enAdmin from './locales/en/admin.json';
import enGovernance from './locales/en/governance.json';
import enLegal from './locales/en/legal.json';
import enSupport from './locales/en/support.json';
import enMarketing from './locales/en/marketing.json';
import enMisc from './locales/en/misc.json';
import enOperations from './locales/en/operations.json';

import nlCommon from './locales/nl/common.json';
import nlNav from './locales/nl/nav.json';
import nlAuth from './locales/nl/auth.json';
import nlDashboard from './locales/nl/dashboard.json';
import nlBilling from './locales/nl/billing.json';
import nlChat from './locales/nl/chat.json';
import nlProfile from './locales/nl/profile.json';
import nlConnections from './locales/nl/connections.json';
import nlAdmin from './locales/nl/admin.json';
import nlGovernance from './locales/nl/governance.json';
import nlLegal from './locales/nl/legal.json';
import nlSupport from './locales/nl/support.json';
import nlMarketing from './locales/nl/marketing.json';
import nlMisc from './locales/nl/misc.json';
import nlOperations from './locales/nl/operations.json';

// Bridge to the existing language preference key written by useLanguage().
// English is the default — we only switch to NL when the user has flipped
// the toggle (persisted in localStorage). Browser locale is intentionally
// ignored so visitors land on the canonical English copy first.
const STORAGE_KEY = 'language-preference';
function getInitialLanguage(): 'en' | 'nl' {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'en' || stored === 'nl') return stored;
  return 'en';
}

void i18n.use(initReactI18next).init({
  lng: getInitialLanguage(),
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: [
    'common',
    'nav',
    'auth',
    'dashboard',
    'billing',
    'chat',
    'profile',
    'connections',
    'admin',
    'governance',
    'legal',
    'support',
    'marketing',
    'misc',
    'operations',
  ],
  resources: {
    en: {
      common: enCommon,
      nav: enNav,
      auth: enAuth,
      dashboard: enDashboard,
      billing: enBilling,
      chat: enChat,
      profile: enProfile,
      connections: enConnections,
      admin: enAdmin,
      governance: enGovernance,
      legal: enLegal,
      support: enSupport,
      marketing: enMarketing,
      misc: enMisc,
      operations: enOperations,
    },
    nl: {
      common: nlCommon,
      nav: nlNav,
      auth: nlAuth,
      dashboard: nlDashboard,
      billing: nlBilling,
      chat: nlChat,
      profile: nlProfile,
      connections: nlConnections,
      admin: nlAdmin,
      governance: nlGovernance,
      legal: nlLegal,
      support: nlSupport,
      marketing: nlMarketing,
      misc: nlMisc,
      operations: nlOperations,
    },
  },
  interpolation: {
    escapeValue: false, // React already escapes
    // Catalogue counts, interpolated into copy across marketing, billing and
    // auth ("{{toolCount}}+ MCP tools", "{{skillCount}} domain skills").
    //
    // These MUST come from the generated module, never from a literal here.
    // The literal is the bug this replaced: two numbers typed by hand, one of
    // which was eight short of the open-source repo. A runtime fetch of
    // /modules.json used to overwrite them after init — but i18next reads
    // defaultVariables at translation time and React does not re-render for a
    // mutated options object, so first paint always won and the "refresh" was
    // dead code. On top of that the manifest it read shipped `counts: null`
    // in production, because the generator resolved them from a sibling
    // checkout that exists on nobody's build machine.
    //
    // Build-time is also the right layer on the merits: this is copy, and copy
    // has to be correct in the first frame. See portal/scripts/count-catalog.ts.
    defaultVariables: {
      toolCount: CATALOG_COUNTS.toolCount,
      skillCount: CATALOG_COUNTS.skillCount,
    },
  },
  returnNull: false,
  react: {
    // Translations write the markup they need literally (<strong>SERVICENOW_USERNAME</strong>,
    // <code>tool_search</code>) instead of the positional <0>/<1> placeholders,
    // which count children — including whitespace expressions — and silently
    // render the wrong node when the JSX is reformatted. 'code' is the only
    // addition to the react-i18next default list.
    transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p', 'code'],
  },
});

// There is deliberately no runtime refresh of the catalogue counts here. The
// one that used to live at this spot fetched /modules.json and mutated
// i18n.options.interpolation.defaultVariables — which changes nothing already
// on screen, because nothing subscribes to that object. Anything it did fix
// would have flickered. The numbers are compiled in above.

// Keep <html lang> in sync on language change so screen readers + the
// browser pick up the active locale (CSS :lang() rules also work).
i18n.on('languageChanged', (lng) => {
  document.documentElement.setAttribute('lang', lng);
});

export default i18n;
